import { useContext, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { sanitizeHref } from '@wix/da-url';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import StripePaymentTerms from '../../_partials/StripePaymentTerms';
import { DASHBOARD_LOGIN_URL, DASHBOARD_EXPRESS_URL } from '../consts';
import ModalFooterMobile from '@wix/da-ds/pkg/modals/dsPresets/ModalFooterMobile';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import s from './StripeWithdrawConfirmationModal.scss';

export interface Props {
  isOpen: boolean;
  requestClose: () => void;
  onDone?: () => void;
}

const StripeWithdrawConfirmationModal: FC<Props> = ({ isOpen, requestClose, onDone }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const handleDone = () => {
    onDone?.();
    requestClose();
  };

  return (
    <Modal
      className={s['wrapper']}
      contentClassName={s['content']}
      heading={t('studio.earnings.stripe.confirmation.title')}
      onRequestClose={requestClose}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick
      isOpen={isOpen}
      withScrollableContentWrapper
      footerRight={[
        <BrandedButton onClick={handleDone} size="large" variant="brand-green" key="done">
          {t('common.gotIt')}
        </BrandedButton>,
      ]}
      footerPadding={isMobile ? 'none' : undefined}
      footer={
        isMobile ? (
          <ModalFooterMobile
            primaryButton={
              <BrandedButton
                fillContainerWidth={true}
                onClick={handleDone}
                size="large"
                variant="brand-green"
                key="done"
              >
                {t('common.gotIt')}
              </BrandedButton>
            }
          />
        ) : undefined
      }
    >
      <div className={s['subtitle']}>{t('studio.earnings.stripe.confirmation.subtitle')}</div>

      <div className={s['description']}>
        {t('studio.earnings.stripe.confirmation.description.prefix')}{' '}
        <a
          href={sanitizeHref(DASHBOARD_LOGIN_URL)}
          className="text-link-level-2-primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('studio.earnings.stripe.confirmation.description.loginLink')}
        </a>
        .
      </div>

      <div className={s['dashboard-section']}>
        {t('studio.earnings.stripe.confirmation.dashboard.prefix')}{' '}
        <a
          href={sanitizeHref(DASHBOARD_EXPRESS_URL)}
          className="text-link-level-2-primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('studio.earnings.stripe.confirmation.dashboard.link')}
        </a>{' '}
        {t('studio.earnings.stripe.confirmation.dashboard.suffix')}
      </div>

      <StripePaymentTerms className={s['terms']} />
    </Modal>
  );
};

StripeWithdrawConfirmationModal.displayName = 'StripeWithdrawConfirmationModal';

export default StripeWithdrawConfirmationModal;
